import {
  type BreakdownSelection,
  isSentimentBreakdown,
  isThemeBreakdown,
} from 'features/charts-v2/components/breakdown-selection-utils'
import {
  getRatingBarColor,
  getSentimentBarColor,
  MAX_STACKING_CATEGORIES,
  STACKING_OVERFLOW_LABEL,
} from 'features/charts-v2/constants'
import { chartConfig } from 'features/charts-v2/services/chart-config-service'
import {
  type ChartValue,
  METRICS,
  type MetricType,
  type TimeBucketOption,
} from 'features/charts-v2/types'
import {
  ChartData,
  type SortDirection,
  TransformedChartData,
  type TransformedDataPoint,
} from 'features/charts-v2/types/chart-strategy'
import type { Breakdown } from 'features/charts-v2/types/filter-hash/v2'
import {
  getRidgelineBins,
  type RidgelineMetric,
} from 'features/charts-v2/utils/ridgeline-bins'
import {
  getSankeyColumns,
  isGroupedSankeyBreakdown,
  type SankeyColumn,
} from 'features/charts-v2/utils/sankey-columns'
import { resolveCategoryMappingAxis } from 'features/charts-v2/utils/summary-utils'
import { timestampUtils } from 'features/charts-v2/utils/timestamp-utils'
import { ALIAS_FOR_NULL_VALUES } from 'src/constants/filter-values'

// =============================================================================
// Type Definitions for Transformed Data
// =============================================================================

interface BasicChartDataPoint {
  name: string
  count: number | null
  [key: string]: string | number | boolean | null | undefined
}

interface DualAxisDataPoint {
  name: string
  [key: string]: string | number
}

interface HeatmapDataPoint {
  row: string
  column: string
  sentiment: number
  recordCount: number
  secondaryMetricValue: number
  pctVolumeRow: number
  pctVolumeColumn: number
  color: string
}

export interface RidgelineBin {
  /** Bin position on the metric axis (midpoint for sentiment, star value for rating). */
  x: number
  /** Human-readable bucket range, e.g. "0.3 to 0.4". */
  label: string
  count: number
  /** count / row total (0 when the row is empty). */
  pct: number
}

export interface RidgelineRow {
  /** Display label — a null breakdown value reads "Unknown". */
  name: string
  /** Wire value of the breakdown; null stays null so a click filters on IS_NULL. */
  category: string | null
  /** Total count across the row's bins. */
  n: number
  /** Exact row mean, Σ(avg·count) / Σcount; null for an empty row. */
  mean: number | null
  /** Every bin in the metric's domain, zero-filled. */
  bins: RidgelineBin[]
}

/** How many nodes a Sankey column keeps; the rest fold into one "Other" node. */
const SANKEY_NODES_PER_COLUMN = 8

export interface SankeyNodeDatum extends SankeyColumn {
  /** Column-scoped, so one value in two columns is two nodes. */
  id: string
  /** Index into `SankeyGraph.columns`. */
  column: number
  /** Display label; a grouped theme's member drops its "Parent: " prefix. */
  label: string
  /** Wire value a click filters on; null for the "Other" node. Not `value`,
   *  which Recharts' `Sankey` overwrites with the node's flow. */
  wireValue: string | null
  isOther: boolean
  total: number
  /** Count-weighted average score through the node; null when none carried one. */
  avg: number | null
  /** The first-column node most of this node's volume came from. */
  originId: string
}

export interface SankeyLinkDatum {
  source: string
  target: string
  count: number
  avg: number | null
  /** The first-column node most of this link's volume came from. */
  originId: string
}

export interface SankeyGraph {
  columns: SankeyColumn[]
  nodes: SankeyNodeDatum[]
  links: SankeyLinkDatum[]
}

const EMPTY_SANKEY_GRAPH: SankeyGraph = { columns: [], nodes: [], links: [] }

interface SankeyColumnPlan extends SankeyColumn {
  /** Member -> group, for a 'group' column. */
  groupOf?: Map<string, string>
}

interface SankeyFlowStats {
  count: number
  scoreSum: number
  scoreCount: number
  originCounts: Map<string, number>
}

const toSankeyLabel = (raw: string | number | null | undefined): string =>
  raw == null || raw === '' ? ALIAS_FOR_NULL_VALUES : String(raw)

const planSankeyColumns = (
  chart: ChartData,
  breakdowns: readonly BreakdownSelection[],
): SankeyColumnPlan[] =>
  getSankeyColumns(breakdowns).flatMap((column): SankeyColumnPlan[] => {
    if (column.level === 'value') return [column]
    const mapping = resolveCategoryMappingAxis(
      chart.category_mapping ?? {},
      breakdowns[column.breakdownIndex] ?? null,
    )
    // Without a mapping a grouped breakdown has nothing to group by, so the
    // flow degrades to its member column alone.
    if (Object.keys(mapping).length === 0) return []
    const groupOf = new Map<string, string>()
    Object.entries(mapping).forEach(([group, members]) =>
      members.forEach((member) => groupOf.set(member, group)),
    )
    return [{ ...column, groupOf }]
  })

const newSankeyFlowStats = (): SankeyFlowStats => ({
  count: 0,
  scoreSum: 0,
  scoreCount: 0,
  originCounts: new Map(),
})

const addToSankeyFlow = (
  stats: SankeyFlowStats,
  count: number,
  score: number | null,
  originId: string,
): void => {
  stats.count += count
  if (score != null) {
    stats.scoreSum += score * count
    stats.scoreCount += count
  }
  stats.originCounts.set(
    originId,
    (stats.originCounts.get(originId) ?? 0) + count,
  )
}

const dominantSankeyOrigin = (stats: SankeyFlowStats): string =>
  [...stats.originCounts.entries()].sort(
    ([idA, countA], [idB, countB]) => countB - countA || idA.localeCompare(idB),
  )[0]![0]

const sankeyFlowAverage = (stats: SankeyFlowStats): number | null =>
  stats.scoreCount > 0 ? stats.scoreSum / stats.scoreCount : null

interface GenericDataPoint {
  name: string
  [key: string]: string | number | null
}

interface TreemapDataPoint extends TransformedDataPoint {
  name: string
  size: number
}

export class DataTransformationService {
  private static instance: DataTransformationService

  private constructor() {}

  public static getInstance(): DataTransformationService {
    if (!DataTransformationService.instance) {
      DataTransformationService.instance = new DataTransformationService()
    }
    return DataTransformationService.instance
  }

  /**
   * Check if a metric type is summable (can be aggregated meaningfully)
   */
  public isSummableMetric(metric: MetricType): boolean {
    return (
      metric === METRICS.RECORD_COUNT.id || metric === METRICS.SENTENCE_COUNT.id
    )
  }

  /**
   * Transform data for total-only view (no breakdowns)
   * Used when user removes all breakdowns to show a single aggregated value
   */
  public transformForTotalOnly(
    data: ChartData,
    _selectedMetrics: MetricType[],
  ): TransformedChartData {
    if (!data || !data.data || data.data.length === 0) {
      return { chartData: [] }
    }

    const total = data.data.reduce((sum: number, item: ChartValue) => {
      return sum + (item.values[0] != null ? item.values[0] : 0)
    }, 0)

    return {
      chartData: [
        {
          name: 'Total',
          count: total,
          value: total,
        },
      ],
    }
  }

  /**
   * Transform data for basic chart format (bar, line, area)
   */
  public transformForBasicChart(
    data: ChartData,
    selectedMetrics: MetricType[],
    timeBucket?: TimeBucketOption | null,
    sortDirection?: SortDirection,
  ): TransformedChartData {
    if (!data || !data.data) return { chartData: [] }

    // Volume/count metrics treat a missing bucket as a true 0; sentiment /
    // rating / NPS must stay null so a backfilled empty time bucket renders
    // as a gap in the line, not a misleading dip to 0. The backend time-axis
    // gap-fill emits null for these metrics; coercing it to 0 here would erase
    // that distinction.
    const primaryMetric = selectedMetrics[0]
    const fillMissingWithZero =
      primaryMetric != null && this.isSummableMetric(primaryMetric)

    const transformedData: BasicChartDataPoint[] = data.data.map(
      (item: ChartValue) => {
        const rawName = item.category[0] || ALIAS_FOR_NULL_VALUES

        // If we have a time bucket, format the timestamp
        const formattedName = timestampUtils.formatTimestampForBucket(
          rawName,
          timeBucket,
        )

        return {
          name: formattedName,
          // Preserve the raw timestamp for accurate date range parsing
          rawTimestamp: rawName,
          // Use the first value as the count - don't default to 0 for negative
          // values, and keep null for non-volume metrics so gaps stay gaps.
          count:
            item.values[0] != null
              ? item.values[0]
              : fillMissingWithZero
                ? 0
                : null,
          // Preserve the augmented volume count (values[1]) when the API augments
          // sentiment/rating requests with companion sentence_count/record_count
          ...(item.values[1] != null ? { volumeCount: item.values[1] } : {}),
        }
      },
    )

    if (timeBucket) {
      transformedData.sort(
        (a, b) =>
          new Date(a['rawTimestamp'] as string).getTime() -
          new Date(b['rawTimestamp'] as string).getTime(),
      )
    } else if (sortDirection) {
      transformedData.sort((a, b) =>
        sortDirection === 'ASC'
          ? (a.count ?? 0) - (b.count ?? 0)
          : (b.count ?? 0) - (a.count ?? 0),
      )
    }

    return { chartData: transformedData }
  }

  /**
   * Transform ridgeline (distribution) data into one row per breakdown value.
   *
   * Wire shape: each item is `category = [<breakdown>, <bin>]` (just `[<bin>]`
   * with no breakdown) and `values = [count, avg]` — the count of sentences
   * (sentiment) or records (rating) in that cell, and the average score within
   * it. The row mean is the exact Σ(avg·count)/Σcount, not a midpoint estimate,
   * so it matches the horizontal bar's value for the same row.
   *
   * Rows are sorted by that mean like `transformForBasicChart`; empty rows
   * (no mean) always sort last.
   */
  public transformForRidgeline(
    data: ChartData,
    metric: RidgelineMetric,
    sortDirection?: SortDirection,
  ): RidgelineRow[] {
    if (!data || !data.data) return []
    const { rows } = this.buildRidgelineRows(data, metric)

    if (sortDirection) {
      const direction = sortDirection === 'ASC' ? 1 : -1
      rows.sort((a, b) => {
        if (a.mean == null || b.mean == null) {
          return (a.mean == null ? 1 : 0) - (b.mean == null ? 1 : 0)
        }
        return (a.mean - b.mean) * direction
      })
    }
    return rows
  }

  /**
   * Collapse ridgeline wire rows (`[breakdown, bin]` → `[count, avg]`) into the
   * `[breakdown]` → `[mean, count]` shape a horizontal bar of the same metric
   * would receive, so the legend table reads the same as the bar chart's.
   */
  public summarizeRidgelineChart(
    data: ChartData,
    metric: RidgelineMetric,
  ): ChartData {
    const { rows, hasBreakdown } = this.buildRidgelineRows(data, metric)
    return {
      ...data,
      data: rows.map((row) => ({
        category: hasBreakdown ? [row.category ?? ''] : [],
        values: [row.mean ?? 0, row.n],
      })),
      parent_data: null,
    }
  }

  private buildRidgelineRows(
    data: ChartData,
    metric: RidgelineMetric,
  ): { rows: RidgelineRow[]; hasBreakdown: boolean } {
    const bins = getRidgelineBins(metric)
    const binPosition = new Map(
      bins.map((bin, position) => [bin.key, position]),
    )
    const hasBreakdown = data.data.some((item) => item.category.length > 1)

    interface Cell {
      count: number
      weightedSum: number
    }
    const cellsByRow = new Map<string | null, Cell[]>()

    data.data.forEach((item) => {
      const rawCategory = hasBreakdown ? item.category[0] : null
      const category =
        rawCategory == null || rawCategory === '' ? null : String(rawCategory)
      const position = binPosition.get(
        Number(item.category[item.category.length - 1]),
      )
      if (position === undefined) return

      let cells = cellsByRow.get(category)
      if (!cells) {
        cells = bins.map(() => ({ count: 0, weightedSum: 0 }))
        cellsByRow.set(category, cells)
      }
      const count = item.values[0] ?? 0
      const avg = item.values[1] ?? 0
      cells[position]!.count += count
      cells[position]!.weightedSum += avg * count
    })

    const rows = Array.from(cellsByRow, ([category, cells]): RidgelineRow => {
      const n = cells.reduce((sum, cell) => sum + cell.count, 0)
      const weightedSum = cells.reduce((sum, cell) => sum + cell.weightedSum, 0)
      return {
        name: category ?? (hasBreakdown ? ALIAS_FOR_NULL_VALUES : 'Total'),
        category,
        n,
        mean: n > 0 ? weightedSum / n : null,
        bins: bins.map((bin, position) => ({
          x: bin.x,
          label: bin.label,
          count: cells[position]!.count,
          pct: n > 0 ? cells[position]!.count / n : 0,
        })),
      }
    })
    return { rows, hasBreakdown }
  }

  /**
   * Transform Sankey rows into a column-scoped node/link graph.
   *
   * Wire shape: each item is `category = [<breakdown>, ...]` in panel order and
   * `values = [count, avg]`. Each row is one path through the columns; a
   * grouped breakdown contributes two columns (group, then member) by mapping
   * the member to its group through `category_mapping`.
   *
   * A column keeps its top nodes by volume and folds the rest into "Other". A
   * link's average is weighted by count: exact for sentence counts (the BE
   * weights `avg` by sentences), approximate for record counts.
   */
  public transformForSankey(
    chart: ChartData,
    selectedBreakdowns: readonly BreakdownSelection[],
  ): SankeyGraph {
    if (!chart || !chart.data) return EMPTY_SANKEY_GRAPH
    const columns = planSankeyColumns(chart, selectedBreakdowns)
    if (columns.length < 2) return EMPTY_SANKEY_GRAPH

    const paths = chart.data.flatMap((item) => {
      const count = item.values[0] ?? 0
      if (count <= 0) return []
      return [
        {
          count,
          score: item.values[1] ?? null,
          labels: columns.map((column) => {
            const member = toSankeyLabel(item.category[column.breakdownIndex])
            return column.groupOf?.get(member) ?? member
          }),
        },
      ]
    })

    const keptLabels = columns.map((_, columnIndex) => {
      const totals = new Map<string, number>()
      paths.forEach(({ labels, count }) =>
        totals.set(
          labels[columnIndex]!,
          (totals.get(labels[columnIndex]!) ?? 0) + count,
        ),
      )
      return new Set(
        [...totals.entries()]
          .sort(
            ([labelA, totalA], [labelB, totalB]) =>
              totalB - totalA || labelA.localeCompare(labelB),
          )
          .slice(0, SANKEY_NODES_PER_COLUMN)
          .map(([label]) => label),
      )
    })

    const nodeStats = new Map<
      string,
      SankeyFlowStats & Pick<SankeyNodeDatum, 'column' | 'label' | 'wireValue'>
    >()
    const linkStats = new Map<
      string,
      SankeyFlowStats & Pick<SankeyLinkDatum, 'source' | 'target'>
    >()
    const memberDisplayLabel = (columnIndex: number, label: string): string => {
      const column = columns[columnIndex]!
      const breakdown = selectedBreakdowns[column.breakdownIndex] ?? null
      const followsGroup =
        column.level === 'value' &&
        columns[columnIndex - 1]?.breakdownIndex === column.breakdownIndex
      const prefixEnd = label.indexOf(': ')
      return isThemeBreakdown(breakdown) &&
        isGroupedSankeyBreakdown(breakdown) &&
        followsGroup &&
        prefixEnd !== -1
        ? label.slice(prefixEnd + 2)
        : label
    }

    paths.forEach(({ labels, count, score }) => {
      const nodeIds = labels.map((label, columnIndex) => {
        const kept = keptLabels[columnIndex]!.has(label)
        const id = kept
          ? `${columnIndex}::v::${label}`
          : `${columnIndex}::other`
        if (!nodeStats.has(id)) {
          nodeStats.set(id, {
            ...newSankeyFlowStats(),
            column: columnIndex,
            label: kept
              ? memberDisplayLabel(columnIndex, label)
              : STACKING_OVERFLOW_LABEL,
            wireValue: kept ? label : null,
          })
        }
        return id
      })
      const originId = nodeIds[0]!
      nodeIds.forEach((id, columnIndex) => {
        addToSankeyFlow(nodeStats.get(id)!, count, score, originId)
        const target = nodeIds[columnIndex + 1]
        if (target === undefined) return
        const linkId = `${id}->${target}`
        if (!linkStats.has(linkId)) {
          linkStats.set(linkId, {
            ...newSankeyFlowStats(),
            source: id,
            target,
          })
        }
        addToSankeyFlow(linkStats.get(linkId)!, count, score, originId)
      })
    })

    const nodes = [...nodeStats.entries()]
      .map(([id, stats]): SankeyNodeDatum => {
        const column = columns[stats.column]!
        return {
          id,
          column: stats.column,
          breakdownIndex: column.breakdownIndex,
          level: column.level,
          label: stats.label,
          wireValue: stats.wireValue,
          isOther: stats.wireValue === null,
          total: stats.count,
          avg: sankeyFlowAverage(stats),
          originId: dominantSankeyOrigin(stats),
        }
      })
      .sort(
        (a, b) =>
          a.column - b.column ||
          b.total - a.total ||
          a.label.localeCompare(b.label),
      )
    const links = [...linkStats.values()]
      .map(
        (stats): SankeyLinkDatum => ({
          source: stats.source,
          target: stats.target,
          count: stats.count,
          avg: sankeyFlowAverage(stats),
          originId: dominantSankeyOrigin(stats),
        }),
      )
      .sort((a, b) => b.count - a.count)

    return {
      columns: columns.map(({ breakdownIndex, level }) => ({
        breakdownIndex,
        level,
      })),
      nodes,
      links,
    }
  }

  /**
   * Transform data for dual-axis charts (bar + line)
   * Used when multiple metrics are selected for BAR chart type
   */
  public transformForDualAxisChart(
    data: ChartData,
    selectedMetrics: MetricType[],
    timeBucket?: TimeBucketOption | null,
  ): TransformedChartData {
    if (!data || !data.data) return { chartData: [] }

    const transformedData: DualAxisDataPoint[] = data.data.map(
      (item: ChartValue) => {
        const rawName = item.category[0] || ALIAS_FOR_NULL_VALUES

        // If we have a time bucket, format the timestamp
        const formattedName = timestampUtils.formatTimestampForBucket(
          rawName,
          timeBucket,
        )

        const dataPoint: DualAxisDataPoint = {
          name: formattedName,
          // Preserve the raw timestamp for accurate date range parsing
          rawTimestamp: rawName,
        }

        // Map each metric to a separate data key
        selectedMetrics.forEach((_metric, index) => {
          const value = item.values[index] != null ? item.values[index] : 0
          dataPoint[`metric${index + 1}`] = value
        })

        return dataPoint
      },
    )

    if (timeBucket) {
      transformedData.sort(
        (a, b) =>
          new Date(a['rawTimestamp'] as string).getTime() -
          new Date(b['rawTimestamp'] as string).getTime(),
      )
    }

    return {
      chartData: transformedData,
      metadata: {
        metricCount: selectedMetrics.length,
      },
    }
  }

  /**
   * Transform data for treemap format
   * Supports up to 4 metrics - first metric determines tile size, all metrics are rendered as annotations
   */
  public transformForTreemap(
    data: ChartData,
    selectedMetrics: MetricType[],
    timeBucket?: TimeBucketOption | null,
  ): TransformedChartData {
    if (!data || !data.data) return { chartData: [] }

    const chartData = data.data
      .map((item: ChartValue) => {
        const rawName =
          item.category?.[0] != null ? item.category[0] : ALIAS_FOR_NULL_VALUES
        const values = item.values || []

        // Format the name using timestamp utility if time bucket is provided
        const name = timestampUtils.formatTimestampForBucket(
          rawName,
          timeBucket,
        )

        // Primary metric (index 0) determines size
        const primaryValue = values[0] != null ? values[0] : 0

        // Build result object with all metric values
        const result: TreemapDataPoint = {
          name,
          size: Math.abs(primaryValue), // Size determines the rectangle size - use absolute value
        }

        // Store all metric values with their metric type as key
        selectedMetrics.forEach((metricType, idx) => {
          const value = values[idx]
          if (value != null) {
            result[`metric_${metricType}`] = value
          }
        })

        return result
      })
      .filter((item) => item.size !== 0) // Only exclude items with exactly zero size
      .sort((a, b) => b.size - a.size) // Sort by size descending

    return { chartData }
  }

  /**
   * Transform data for ordered stacked bar chart format
   * First breakdown becomes X-axis, second breakdown becomes stacking categories
   *
   * This method trusts that the backend returns data in the same order as requested:
   * - category[0] corresponds to first breakdown (X-axis)
   * - category[1] corresponds to second breakdown (stacking)
   */
  public transformForOrderedStackedBar(
    data: ChartData,
    breakdowns: string[],
    _selectedMetrics: MetricType[],
    timeBucket?: TimeBucketOption | null,
    sortDirection: SortDirection = 'DESC',
  ): TransformedChartData {
    if (!data || !data.data || breakdowns.length < 2) {
      return {
        chartData: [],
        metadata: { breakdownCategories: [], breakdownCategoryLabels: [] },
      }
    }

    const filteredData = data.data

    // Validate that data structure matches expected breakdowns
    // This prevents race conditions where stale data with wrong dimensions is used
    const expectedDimensions = breakdowns.length
    const actualDimensions = filteredData[0]?.category?.length || 0

    if (actualDimensions < expectedDimensions) {
      // Data structure doesn't match config - return empty until correct data loads
      return {
        chartData: [],
        metadata: { breakdownCategories: [], breakdownCategoryLabels: [] },
      }
    }

    // Trust the breakdown order: first = X-axis, second = stacking
    // The backend should return data with category[0] = first breakdown, category[1] = second breakdown

    // Helper to safely convert category values to strings
    // Category values can be strings, numbers, or UUIDs from the backend
    const toStringValue = (
      value: string | number | null | undefined,
    ): string => {
      if (value === null || value === undefined) return ALIAS_FOR_NULL_VALUES
      return String(value)
    }

    // Collect all unique values for each breakdown position (converted to strings)
    const xAxisValues = Array.from(
      new Set(
        filteredData.map((item: ChartValue) =>
          toStringValue(item.category?.[0]),
        ),
      ),
    ).sort()

    const stackingValues = Array.from(
      new Set(
        filteredData.map((item: ChartValue) =>
          toStringValue(item.category?.[1]),
        ),
      ),
    )

    // Calculate totals for each stacking category to sort by occurrence
    // Optimized single-pass computation instead of O(n*m)
    const categoryTotals: { [key: string]: number } = {}
    for (const item of filteredData) {
      const category = toStringValue(item.category?.[1])
      categoryTotals[category] =
        (categoryTotals[category] ?? 0) + (item.values?.[0] ?? 0)
    }

    // Helper to extract numeric rating from category strings (e.g., '5 Stars' -> 5, '5' -> 5)
    const extractRatingNumber = (category: string): number => {
      const match = String(category).match(/^(\d)/)
      return match ? parseInt(match[1]!, 10) : 0
    }

    // Check if second breakdown is rating/sentiment to apply special sorting
    const isRatingBreakdown = breakdowns[1] === 'rating'
    const isSentimentBreakdownStack = breakdowns[1] === 'sentiment'

    if (isRatingBreakdown) {
      // Sort ratings 5 → 1 (descending) for consistent visual ordering
      // This ensures dark green (5 stars) is always on the left, dark red (1 star) on the right
      stackingValues.sort((a, b) => {
        const ratingA = extractRatingNumber(a)
        const ratingB = extractRatingNumber(b)
        return ratingB - ratingA // Descending: 5, 4, 3, 2, 1
      })
    } else if (isSentimentBreakdownStack) {
      // Fixed order regardless of occurrence: Positive, Neutral, Negative.
      // Case-insensitive match — BUCKET_SENTIMENT emits Title Case buckets.
      const sentimentOrder: { [key: string]: number } = {
        positive: 0,
        neutral: 1,
        negative: 2,
      }
      stackingValues.sort(
        (a, b) =>
          (sentimentOrder[a.toLowerCase()] ?? 99) -
          (sentimentOrder[b.toLowerCase()] ?? 99),
      )
    } else {
      // Sort by total occurrence DESC (largest first = leftmost in bar)
      stackingValues.sort(
        (a, b) => (categoryTotals[b] ?? 0) - (categoryTotals[a] ?? 0),
      )
    }

    // Cap stacking categories to prevent browser freeze/crash with hundreds of products
    // Keep top N categories + aggregate the rest into STACKING_OVERFLOW_LABEL
    let overflowCategories: string[] = []

    if (!isRatingBreakdown && stackingValues.length > MAX_STACKING_CATEGORIES) {
      const topCategories = stackingValues.slice(0, MAX_STACKING_CATEGORIES)
      overflowCategories = stackingValues.slice(MAX_STACKING_CATEGORIES)
      stackingValues.length = 0
      stackingValues.push(...topCategories, STACKING_OVERFLOW_LABEL)
    }

    // Recharts treats string dataKeys as property paths. Backend category
    // labels such as "__proto__", "a.b", or "[0]" are therefore unsafe as
    // row keys. Keep display labels in metadata and use opaque keys internally.
    const stackingKeys = stackingValues.map((_, index) => `stack_${index}`)

    // Group data by the X-axis breakdown (category[0])
    const xAxisGroups: { [key: string]: ChartValue[] } = {}

    filteredData.forEach((item: ChartValue) => {
      const xAxisKey = toStringValue(item.category?.[0])
      if (!xAxisGroups[xAxisKey]) {
        xAxisGroups[xAxisKey] = []
      }
      xAxisGroups[xAxisKey].push(item)
    })

    // Transform into chart data format
    const chartData = xAxisValues
      .map((xAxisKey) => {
        const xAxisItems = xAxisGroups[xAxisKey] || []
        const formattedName = timestampUtils.formatTimestampForBucket(
          xAxisKey,
          timeBucket,
        )

        const dataPoint: GenericDataPoint = {
          name: formattedName,
          // Preserve the raw timestamp for accurate date range parsing
          rawTimestamp: xAxisKey,
        }

        // For each stacking category, sum up the values
        stackingValues.forEach((stackingCategory, index) => {
          const stackingKey = stackingKeys[index]!
          if (stackingCategory === STACKING_OVERFLOW_LABEL) {
            // Sum all overflow categories into the overflow bucket
            const overflowValue = xAxisItems.reduce((sum, item) => {
              const itemCategory = toStringValue(item.category?.[1])
              if (overflowCategories.includes(itemCategory)) {
                const value = item.values?.[0] || 0
                return sum + Math.max(0, value)
              }
              return sum
            }, 0)
            dataPoint[stackingKey] = overflowValue
          } else {
            const categoryItems = xAxisItems.filter(
              (item) => toStringValue(item.category?.[1]) === stackingCategory,
            )

            // Sum up the metric values for this category
            const categoryValue = categoryItems.reduce((sum, item) => {
              const value = item.values?.[0] || 0
              return sum + Math.max(0, value) // Ensure non-negative for stacking
            }, 0)

            dataPoint[stackingKey] = categoryValue
          }
        })

        return dataPoint
      })
      .filter((dataPoint) => {
        // Only include data points that have some data
        const total = stackingKeys.reduce(
          (sum, key) => sum + (Number(dataPoint[key]) || 0),
          0,
        )
        return total > 0
      })
      // Sort by time for time-series data, otherwise by total value based on sortDirection
      .sort((a, b) => {
        if (timeBucket) {
          return (
            new Date(a['rawTimestamp'] as string).getTime() -
            new Date(b['rawTimestamp'] as string).getTime()
          )
        }
        // Sort by total value of the first/parent breakdown (sum of all stacking values)
        const totalA = stackingKeys.reduce(
          (sum, key) => sum + ((a[key] as number) || 0),
          0,
        )
        const totalB = stackingKeys.reduce(
          (sum, key) => sum + ((b[key] as number) || 0),
          0,
        )
        return sortDirection === 'ASC' ? totalA - totalB : totalB - totalA
      })

    return {
      chartData,
      metadata: {
        breakdownCategories: stackingKeys,
        breakdownCategoryLabels: stackingValues,
      },
    }
  }

  /**
   * Transform data for stacked bar chart format (legacy sentiment-specific)
   */
  public transformForStackedBar(data: ChartData): TransformedChartData {
    if (!data || !data.data) return { chartData: [] }

    const chartData = data.data
      .map((item: ChartValue) => {
        const name =
          item.category?.[0] != null ? item.category[0] : ALIAS_FOR_NULL_VALUES
        const values = item.values || []

        // For stacked bar, we expect 3 values: positive, neutral, negative sentiment counts
        const positive = values[0] != null ? Math.max(0, values[0]) : 0
        const neutral = values[1] != null ? Math.max(0, values[1]) : 0
        const negative = values[2] != null ? Math.max(0, values[2]) : 0
        const total = positive + neutral + negative

        return {
          name,
          positive,
          neutral,
          negative,
          total,
        }
      })
      .filter((item) => item.total > 0) // Only include items with some data
    // Preserve the order from input data (sorting is handled by Charts-2.tsx)

    return { chartData }
  }

  /**
   * Transform data for stacked area chart format
   */
  public transformForStackedArea(
    data: ChartData,
    _selectedMetrics: MetricType[],
    timeBucket?: TimeBucketOption | null,
  ): TransformedChartData {
    if (!data || !data.data)
      return { chartData: [], metadata: { breakdownCategories: [] } }

    // Group data by the first category (time for time-series, or primary breakdown)
    const timeGroups: { [key: string]: ChartValue[] } = {}

    data.data.forEach((item: ChartValue) => {
      const timeKey = item.category?.[0] || ALIAS_FOR_NULL_VALUES
      if (!timeGroups[timeKey]) {
        timeGroups[timeKey] = []
      }
      timeGroups[timeKey].push(item)
    })

    // Get all unique breakdown categories from the second category position
    const breakdownCategories: string[] = Array.from(
      new Set(
        data.data
          .map((item: ChartValue) =>
            String(item.category?.[1] ?? ALIAS_FOR_NULL_VALUES),
          )
          .filter(Boolean),
      ),
    ).sort()

    // If no breakdown categories found, use a single category
    if (breakdownCategories.length === 0) {
      breakdownCategories.push('Total')
    }

    // Transform into chart data format
    const chartData = Object.keys(timeGroups)
      .map((timeKey) => {
        const timeItems = timeGroups[timeKey]!
        const formattedName = timestampUtils.formatTimestampForBucket(
          timeKey,
          timeBucket,
        )

        const dataPoint: GenericDataPoint = {
          name: formattedName,
          // Preserve the raw timestamp for accurate date range parsing
          rawTimestamp: timeKey,
        }

        // For each breakdown category, sum up the values
        breakdownCategories.forEach((category) => {
          const categoryItems = timeItems.filter(
            (item: ChartValue) =>
              String(item.category?.[1] ?? ALIAS_FOR_NULL_VALUES) === category,
          )

          // Sum up the metric values for this category
          const categoryValue = categoryItems.reduce((sum, item) => {
            const value = item.values?.[0] || 0
            return sum + Math.max(0, value) // Ensure non-negative for stacking
          }, 0)

          dataPoint[category] = categoryValue
        })

        return dataPoint
      })
      .sort((a, b) => {
        // Sort by time if time bucket is used
        if (timeBucket) {
          return (
            new Date(a['rawTimestamp'] as string).getTime() -
            new Date(b['rawTimestamp'] as string).getTime()
          )
        }
        // Otherwise sort by total value
        const totalA = breakdownCategories.reduce(
          (sum, cat) => sum + (Number(a[cat]) || 0),
          0,
        )
        const totalB = breakdownCategories.reduce(
          (sum, cat) => sum + (Number(b[cat]) || 0),
          0,
        )
        return totalB - totalA
      })

    return {
      chartData,
      metadata: { breakdownCategories: breakdownCategories as string[] },
    }
  }

  /**
   * Transform data for bubble chart format
   */
  public transformForBubbleChart(
    data: ChartData,
    selectedMetrics: MetricType[],
    timeBucket?: TimeBucketOption | null,
    allMetrics?: MetricType[],
  ): TransformedChartData {
    if (!data || !data.data) return { chartData: [] }

    // Find indices of count metrics in the full metrics array (if provided)
    const recordCountIdx = allMetrics
      ? allMetrics.indexOf(METRICS.RECORD_COUNT.id)
      : -1
    const sentenceCountIdx = allMetrics
      ? allMetrics.indexOf(METRICS.SENTENCE_COUNT.id)
      : -1

    const chartData = data.data
      .map((item: ChartValue) => {
        const rawName = item.category?.[0] || ALIAS_FOR_NULL_VALUES
        const values = item.values || []

        // Format the name using timestamp utility if time bucket is provided
        const name = timestampUtils.formatTimestampForBucket(
          rawName,
          timeBucket,
        )

        // Dynamic mapping based on selectedMetrics order
        // 1st metric = X-axis, 2nd metric = Y-axis, 3rd metric = bubble size
        // When user selected only 2 metrics, all bubbles use a constant size
        // (values[2] would be an augmented count metric, not user intent).
        const xValue = values[0] // X-axis: 1st metric
        const yValue = values[1] // Y-axis: 2nd metric
        const zValue =
          selectedMetrics.length >= 3 && values[2] != null ? values[2] : 1

        // Extract count metrics for weighted averaging (if available in allMetrics)
        const recordCount =
          recordCountIdx >= 0 ? values[recordCountIdx] : undefined
        const sentenceCount =
          sentenceCountIdx >= 0 ? values[sentenceCountIdx] : undefined

        return {
          name,
          x: xValue,
          y: yValue,
          z: Math.abs(zValue) || 1, // Ensure positive bubble size
          recordCount,
          sentenceCount,
          // Keep original field names for backward compatibility in tooltips
          values: values,
          metricOrder: selectedMetrics, // Store the metric order for labeling
        }
      })
      .filter((item) => item.x != null && item.y != null && item.z > 0)
      .sort((a, b) => b.z - a.z)

    return { chartData: chartData as unknown as TransformedDataPoint[] }
  }

  /**
   * Transform data for scatter plot format
   */
  public transformForScatterPlot(
    data: ChartData,
    selectedMetrics: MetricType[],
    timeBucket?: TimeBucketOption | null,
  ): TransformedChartData {
    if (!data || !data.data) return { chartData: [] }

    // For scatter plots, we need at least 2 metrics
    if (selectedMetrics.length < 2) {
      return { chartData: [] }
    }

    const chartData = data.data
      .map((item: ChartValue, index: number) => {
        const rawName = item.category?.[0] || `Theme ${index + 1}`
        const values = item.values || []

        // Format the name using timestamp utility if time bucket is provided
        const name = timestampUtils.formatTimestampForBucket(
          rawName,
          timeBucket,
        )

        // Dynamic mapping based on selectedMetrics order
        // 1st metric = X-axis, 2nd metric = Y-axis
        const xValue = values[0] // X-axis: 1st metric
        const yValue = values[1] // Y-axis: 2nd metric

        return {
          name,
          x: xValue,
          y: yValue,
          z: 1, // Fixed size for scatter plot
          // Keep original field names for backward compatibility in tooltips
          values: values,
          metricOrder: selectedMetrics, // Store the metric order for labeling
        }
      })
      .filter((item) => item.x != null && item.y != null)
      .filter((item) => item.x !== 0 || item.y !== 0)
      .sort((a, b) => a.name.localeCompare(b.name))

    return { chartData: chartData as unknown as TransformedDataPoint[] }
  }

  /**
   * Transform data for pie chart format
   */
  public transformForPieChart(
    data: ChartData,
    selectedMetrics: MetricType[],
    timeBucket?: TimeBucketOption | null,
  ): TransformedChartData {
    if (!data || !data.data) return { chartData: [] }

    // Only allow summable metrics for pie charts
    const summableMetrics = selectedMetrics.filter((metric) =>
      this.isSummableMetric(metric),
    )
    if (summableMetrics.length === 0) return { chartData: [] }

    const transformedData = data.data
      .map((item: ChartValue) => {
        const rawName =
          item.category?.[0] != null ? item.category[0] : ALIAS_FOR_NULL_VALUES

        // If we have a time bucket, format the timestamp
        const formattedName = timestampUtils.formatTimestampForBucket(
          rawName,
          timeBucket,
        )

        // Use the first value (should be the summable metric)
        const value = item.values[0] != null ? Math.max(0, item.values[0]) : 0

        return {
          name: formattedName,
          value,
          originalValue: item.values[0] != null ? item.values[0] : 0,
        }
      })
      .filter((item) => item.value > 0) // Only include items with positive values

    // Calculate total for percentage calculations
    const total = transformedData.reduce((sum, item) => sum + item.value, 0)

    if (total === 0) return { chartData: [] }

    // Add percentage calculations and color assignments
    const chartData = transformedData
      .map((item, index) => ({
        ...item,
        percentage: (item.value / total) * 100,
        fill: chartConfig.getColor('pie', index),
      }))
      .sort((a, b) => b.value - a.value) // Sort by value descending

    return { chartData }
  }

  /**
   * Color scale function for heatmap sentiment mapping
   * Uses shared sentiment color logic from constants
   */
  public getSentimentColor(sentiment: number): string {
    return getSentimentBarColor(sentiment)
  }

  /**
   * Color scale function for rating values (1-5 scale)
   * Uses shared rating color logic from constants
   */
  public getRatingColor(rating: number): string {
    return getRatingBarColor(rating)
  }

  /**
   * Color scale function for count-based metrics (Records, Sentences)
   * Uses blue gradient where darker = higher values
   */
  public getCountColor(value: number, min: number, max: number): string {
    // Normalize value to 0-1 range
    const normalized = max > min ? (value - min) / (max - min) : 0
    // Blue gradient: lighter (#dbeafe) to darker (#1e40af)
    const r = Math.round(219 - normalized * (219 - 30))
    const g = Math.round(234 - normalized * (234 - 64))
    const b = Math.round(254 - normalized * (254 - 175))
    return `rgb(${r}, ${g}, ${b})`
  }

  /**
   * Transform data for heatmap format
   */
  public transformForHeatmap(
    data: ChartData,
    selectedMetrics: MetricType[],
    primaryMetric: MetricType,
  ): TransformedChartData {
    if (!data || !data.data)
      return {
        chartData: [],
        metadata: { rows: [], columns: [] },
      }

    // Prefer parent_data when available: it contains fully grouped data
    // (parent themes and/or brand groups) produced by backend post-processing.
    const sourceData =
      data.parent_data && data.parent_data.length > 0
        ? data.parent_data
        : data.data

    // Check if we have dual-dimension data [row, column] or single-dimension [row].
    // Admit all 2-D data, including matrices where both axes share the same values
    // (e.g. brand×brand, or both breakdowns ungrouped to the same dimension) —
    // the downstream grid handles diagonal-only data correctly. Only reject
    // genuinely single-dimension data (category.length < 2).
    const hasDualDimension = sourceData.some(
      (item) => item.category && item.category.length >= 2,
    )

    if (!hasDualDimension) {
      return {
        chartData: [],
        metadata: { rows: [], columns: [] },
      }
    }

    // First pass: collect all primary metric values to determine min/max for count-based coloring
    const primaryMetricIndex = selectedMetrics.indexOf(primaryMetric)
    const allPrimaryValues: number[] = []

    sourceData.forEach((item: ChartValue) => {
      const values = item.values || []
      const primaryValue =
        primaryMetricIndex !== -1
          ? Math.abs(values[primaryMetricIndex] || 0)
          : 0
      allPrimaryValues.push(primaryValue)
    })

    const minPrimaryValue = Math.min(...allPrimaryValues)
    const maxPrimaryValue = Math.max(...allPrimaryValues)

    // Determine if we should use sentiment/rating coloring or count-based coloring
    const useSentimentColoring = primaryMetric === METRICS.SENTIMENT.id
    const useRatingColoring = primaryMetric === METRICS.RATING.id

    const heatmapData: HeatmapDataPoint[] = sourceData.map(
      (item: ChartValue) => {
        // Swap: first breakdown (category[0]) goes to X-axis (columns)
        // second breakdown (category[1]) goes to Y-axis (rows)
        const row = String(item.category?.[1] ?? ALIAS_FOR_NULL_VALUES)
        const column = String(item.category?.[0] ?? ALIAS_FOR_NULL_VALUES)
        const values = item.values || []

        const sentimentIndex = selectedMetrics.indexOf(METRICS.SENTIMENT.id)
        const recordCountIndex = selectedMetrics.indexOf(
          METRICS.RECORD_COUNT.id,
        )

        // Secondary metric is the last metric in selectedMetrics
        const secondaryMetricIndex = selectedMetrics.length - 1

        // Get primary metric value
        const primaryMetricValue =
          primaryMetricIndex !== -1 ? values[primaryMetricIndex] || 0 : 0

        const sentiment =
          sentimentIndex !== -1 ? values[sentimentIndex] || 0 : 0
        const recordCount =
          recordCountIndex !== -1
            ? values[recordCountIndex] || 0
            : values[0] || 0
        const secondaryMetricValue =
          secondaryMetricIndex !== -1
            ? Math.abs(values[secondaryMetricIndex] || 0)
            : 0

        // Choose appropriate color based on primary metric type
        let color: string
        if (useSentimentColoring) {
          color = this.getSentimentColor(primaryMetricValue)
        } else if (useRatingColoring) {
          color = this.getRatingColor(primaryMetricValue)
        } else {
          color = this.getCountColor(
            Math.abs(primaryMetricValue),
            minPrimaryValue,
            maxPrimaryValue,
          )
        }

        return {
          row,
          column,
          primaryMetricValue,
          sentiment,
          recordCount: Math.abs(recordCount),
          secondaryMetricValue,
          pctVolumeRow: 0,
          pctVolumeColumn: 0,
          color,
        }
      },
    )

    // Calculate row and column totals for percentage volumes
    const rowTotals = new Map<string, number>()
    const columnTotals = new Map<string, number>()

    heatmapData.forEach((item) => {
      rowTotals.set(
        item.row,
        (rowTotals.get(item.row) || 0) + item.secondaryMetricValue,
      )
      columnTotals.set(
        item.column,
        (columnTotals.get(item.column) || 0) + item.secondaryMetricValue,
      )
    })

    // Update each data point with percentage volumes
    heatmapData.forEach((item) => {
      const rowTotal = rowTotals.get(item.row) || 0
      const columnTotal = columnTotals.get(item.column) || 0
      item.pctVolumeRow =
        rowTotal > 0 ? (item.secondaryMetricValue / rowTotal) * 100 : 0
      item.pctVolumeColumn =
        columnTotal > 0 ? (item.secondaryMetricValue / columnTotal) * 100 : 0
    })

    // Get unique rows and columns for grid layout
    const rows = Array.from(new Set(heatmapData.map((item) => item.row))).sort()
    const columns = Array.from(
      new Set(heatmapData.map((item) => item.column)),
    ).sort()

    return {
      chartData: heatmapData as unknown as TransformedDataPoint[],
      metadata: { rows, columns },
    }
  }

  /**
   * Transform data for grouped histogram format (temporary simulation)
   */
  public transformForHistogram(
    data: ChartData,
    selectedMetrics: MetricType[],
  ): TransformedChartData {
    if (!data || !data.data)
      return {
        chartData: [],
        metadata: { breakdownCategories: [] },
      }

    // Only allow summable metrics for histogram Y-axis
    const summableMetrics = selectedMetrics.filter((metric) =>
      this.isSummableMetric(metric),
    )
    if (summableMetrics.length === 0) {
      return { chartData: [], metadata: { breakdownCategories: [] } }
    }

    // TEMPORARY SIMULATION: Create grouped bars with brands on X-axis and rating/sentiment as grouped bars
    const breakdownCategories = data.data
      .map((item: ChartValue) =>
        String(item.category?.[0] ?? ALIAS_FOR_NULL_VALUES),
      )
      .filter((category, index, self) => self.indexOf(category) === index)
      .sort()

    const ratingBins = ['1 Star', '2 Stars', '3 Stars', '4 Stars', '5 Stars']

    const chartData = breakdownCategories.map((brand) => {
      const brandData: GenericDataPoint = { name: brand }

      // Get the original value for this brand
      const originalDataItem = data.data.find(
        (item) => item.category?.[0] === brand,
      )
      const originalValue = originalDataItem?.values?.[0] || 0

      // Distribute this brand's data across rating bins (fake data for demonstration)
      ratingBins.forEach((rating, ratingIndex) => {
        const distributionFactors = [0.1, 0.15, 0.3, 0.35, 0.1] // Fake distribution
        const binValue = Math.round(
          originalValue * distributionFactors[ratingIndex]!,
        )
        brandData[rating] = binValue
      })

      return brandData
    })

    return {
      chartData,
      metadata: { breakdownCategories: ratingBins },
    }
  }

  /**
   * Transform data for sentiment stacking format
   * This handles data from createStackedSentimentPayload where we have:
   * - Single domain (e.g., Brand)
   * - Multiple aggregated values (positive, neutral, negative counts)
   *
   * The breakdown order determines the chart arrangement:
   * - First breakdown = X-axis
   * - Second breakdown = Stacking categories (sentiment is implicit as values)
   */
  public transformForSentimentStacking(
    data: ChartData,
    _selectedMetrics: MetricType[],
    breakdowns: Breakdown[],
    timeBucket?: TimeBucketOption | null,
    sortDirection: SortDirection = 'DESC',
  ): TransformedChartData {
    if (!data || !data.data) {
      return { chartData: [], metadata: { breakdownCategories: [] } }
    }

    const filteredData = data.data

    const sentimentCategories = ['positive', 'neutral', 'negative']
    const firstBreakdown = breakdowns[0]

    // Check if first breakdown is sentiment
    const isSentimentFirst = isSentimentBreakdown(firstBreakdown ?? null)

    // If sentiment is the first breakdown, put sentiment on X-axis
    if (isSentimentFirst) {
      // Sentiment on X-axis, other breakdown as stacking
      const brandNames = Array.from(
        new Set(
          filteredData.map((item: ChartValue) =>
            String(item.category?.[0] ?? ALIAS_FOR_NULL_VALUES),
          ),
        ),
      )

      // Create data points for each sentiment category
      const chartData = sentimentCategories
        .map((sentiment, sentimentIndex) => {
          const dataPoint: GenericDataPoint = { name: sentiment }

          // For each brand, get the count for this sentiment
          filteredData.forEach((item: ChartValue) => {
            const brandName = String(
              item.category?.[0] ?? ALIAS_FOR_NULL_VALUES,
            )
            const sentimentCount = item.values?.[sentimentIndex] || 0
            dataPoint[brandName] = Math.max(0, sentimentCount)
          })

          return dataPoint
        })
        .sort((a, b) => {
          // Sort by total value across all stacking categories (brands)
          const totalA = brandNames.reduce(
            (sum, brand) => sum + ((a[brand] as number) || 0),
            0,
          )
          const totalB = brandNames.reduce(
            (sum, brand) => sum + ((b[brand] as number) || 0),
            0,
          )
          return sortDirection === 'ASC' ? totalA - totalB : totalB - totalA
        })

      return {
        chartData,
        metadata: { breakdownCategories: brandNames },
      }
    } else {
      // Other breakdown on X-axis, sentiment as stacking
      const chartData = filteredData
        .map((item: ChartValue) => {
          const categoryName = item.category?.[0] || ALIAS_FOR_NULL_VALUES
          const formattedName = timestampUtils.formatTimestampForBucket(
            categoryName,
            timeBucket,
          )

          // Extract the three sentiment values
          const positiveCount = item.values?.[0] || 0
          const neutralCount = item.values?.[1] || 0
          const negativeCount = item.values?.[2] || 0

          return {
            name: formattedName,
            // Preserve the raw timestamp for accurate date range parsing
            rawTimestamp: categoryName,
            positive: Math.max(0, positiveCount), // Ensure non-negative for stacking
            neutral: Math.max(0, neutralCount),
            negative: Math.max(0, negativeCount),
          }
        })
        .filter((dataPoint) => {
          // Only include data points that have some data
          const total =
            (dataPoint.positive || 0) +
            (dataPoint.neutral || 0) +
            (dataPoint.negative || 0)
          return total > 0
        })
        .sort((a, b) => {
          // Sort by time for time-series data, otherwise by total value based on sortDirection
          if (timeBucket) {
            return (
              new Date(a.rawTimestamp as string).getTime() -
              new Date(b.rawTimestamp as string).getTime()
            )
          }
          // Sort by total value across all sentiment categories
          const totalA =
            (a.positive || 0) + (a.neutral || 0) + (a.negative || 0)
          const totalB =
            (b.positive || 0) + (b.neutral || 0) + (b.negative || 0)
          return sortDirection === 'ASC' ? totalA - totalB : totalB - totalA
        })

      return {
        chartData,
        metadata: { breakdownCategories: sentimentCategories },
      }
    }
  }

  /**
   * Transform data for multi-line chart format
   * Domains are now ordered: [breakdown, time] - breakdown first, time second
   *
   * Expected data structure:
   * - category[0] = breakdown value (line series) e.g., "My Brands", "Competitor Brands"
   * - category[1] = time value (X-axis) e.g., "2024-11-01"
   * - values[0] = metric value
   */
  public transformForMultiLineChart(
    data: ChartData,
    _selectedMetrics: MetricType[],
    timeBucket?: TimeBucketOption | null,
  ): TransformedChartData {
    if (!data || !data.data) {
      return { chartData: [], metadata: { breakdownCategories: [] } }
    }

    // Group data by time (SECOND category now, since we swapped the order)
    const timeGroups: { [key: string]: ChartValue[] } = {}

    data.data.forEach((item: ChartValue) => {
      const timeKey = item.category?.[1] || ALIAS_FOR_NULL_VALUES // Changed from [0] to [1]
      if (!timeGroups[timeKey]) {
        timeGroups[timeKey] = []
      }
      timeGroups[timeKey].push(item)
    })

    // Get all unique breakdown categories from the FIRST category position (changed from second)
    const breakdownCategories: string[] = Array.from(
      new Set(
        data.data
          .map((item: ChartValue) =>
            String(item.category?.[0] ?? ALIAS_FOR_NULL_VALUES),
          )
          .filter(Boolean),
      ),
    ).sort()

    // Transform into chart data format
    const chartData = Object.keys(timeGroups)
      .map((timeKey) => {
        const timeItems = timeGroups[timeKey]!
        const formattedName = timestampUtils.formatTimestampForBucket(
          timeKey,
          timeBucket,
        )

        const dataPoint: GenericDataPoint = {
          name: formattedName,
          // Preserve the raw timestamp for accurate date range parsing
          rawTimestamp: timeKey,
        }

        // For each breakdown category, find the corresponding value
        breakdownCategories.forEach((category) => {
          const categoryItem = timeItems.find(
            (item: ChartValue) =>
              String(item.category?.[0] ?? ALIAS_FOR_NULL_VALUES) === category, // Changed from [1] to [0]
          )

          // Use the metric value, default to null for missing data points
          const value = categoryItem?.values?.[0]
          dataPoint[category] = value != null ? value : null
        })

        return dataPoint
      })
      .sort((a, b) => {
        // Sort by time if time bucket is used
        if (timeBucket) {
          return (
            new Date(a['rawTimestamp'] as string).getTime() -
            new Date(b['rawTimestamp'] as string).getTime()
          )
        }
        // Otherwise sort by name
        return a.name.localeCompare(b.name)
      })

    return {
      chartData,
      metadata: { breakdownCategories },
    }
  }

  /**
   * Transform data for dual-axis multi-line charts (line + area per breakdown)
   * Used when multiple metrics AND breakdowns are selected for LINE chart type
   *
   * Data format (when 1 breakdown is selected + 2 metrics):
   * - category[0] = breakdown value (line series) e.g., "My Brands", "Competitor Brands"
   * - category[1] = time value (X-axis) e.g., "2024-11-01"
   * - values[0] = first metric value (rendered as line)
   * - values[1] = second metric value (rendered as area)
   */
  public transformForDualAxisMultiLineChart(
    data: ChartData,
    selectedMetrics: MetricType[],
    timeBucket?: TimeBucketOption | null,
  ): TransformedChartData {
    if (!data || !data.data) {
      return {
        chartData: [],
        metadata: { breakdownCategories: [], metricCount: 2 },
      }
    }

    // Group data by time (SECOND category)
    const timeGroups: { [key: string]: ChartValue[] } = {}

    data.data.forEach((item: ChartValue) => {
      const timeKey = item.category?.[1] || ALIAS_FOR_NULL_VALUES
      if (!timeGroups[timeKey]) {
        timeGroups[timeKey] = []
      }
      timeGroups[timeKey].push(item)
    })

    // Get all unique breakdown categories from the FIRST category position
    const breakdownCategories: string[] = Array.from(
      new Set(
        data.data
          .map((item: ChartValue) =>
            String(item.category?.[0] ?? ALIAS_FOR_NULL_VALUES),
          )
          .filter(Boolean),
      ),
    ).sort()

    // Transform into chart data format
    const chartData = Object.keys(timeGroups)
      .map((timeKey) => {
        const timeItems = timeGroups[timeKey]!
        const formattedName = timestampUtils.formatTimestampForBucket(
          timeKey,
          timeBucket,
        )

        const dataPoint: GenericDataPoint = {
          name: formattedName,
          // Preserve the raw timestamp for accurate date range parsing
          rawTimestamp: timeKey,
        }

        // For each breakdown category, find the corresponding values
        breakdownCategories.forEach((category) => {
          const categoryItem = timeItems.find(
            (item: ChartValue) =>
              String(item.category?.[0] ?? ALIAS_FOR_NULL_VALUES) === category,
          )

          // First metric (line)
          const value1 = categoryItem?.values?.[0]
          dataPoint[category] = value1 != null ? value1 : null

          // Second metric (area) - use __m2 suffix
          const value2 = categoryItem?.values?.[1]
          dataPoint[`${category}__m2`] = value2 != null ? value2 : null
        })

        return dataPoint
      })
      .sort((a, b) => {
        // Sort by time if time bucket is used
        if (timeBucket) {
          return (
            new Date(a['rawTimestamp'] as string).getTime() -
            new Date(b['rawTimestamp'] as string).getTime()
          )
        }
        // Otherwise sort by name
        return a.name.localeCompare(b.name)
      })

    return {
      chartData,
      metadata: { breakdownCategories, metricCount: selectedMetrics.length },
    }
  }

  /**
   * Helper method to detect if data is in sentiment stacking format
   * Sentiment stacking data has:
   * - Single-dimensional categories (length 1)
   * - Multiple values per item (typically 3 for pos/neu/neg)
   */
  public isSentimentStackingData(data: ChartData): boolean {
    if (!data || !data.data || data.data.length === 0) {
      return false
    }

    const firstItem = data.data[0]!

    // Check if we have single-dimensional categories
    const hasSingleDimensionCategory =
      firstItem.category &&
      Array.isArray(firstItem.category) &&
      firstItem.category.length === 1

    // Check if we have multiple values (sentiment stacking typically has 3)
    const hasMultipleValues =
      firstItem.values &&
      Array.isArray(firstItem.values) &&
      firstItem.values.length >= 3

    return hasSingleDimensionCategory && hasMultipleValues
  }
}

export const dataTransformationService = DataTransformationService.getInstance()
